import apiClient from '../apiClient';
import type { AttachmentClassificationResult } from '@/types/attachmentClassifier';

export type AttachmentKind = 'dicom' | 'image' | 'document';
export type AttachmentStatus = 'pending' | 'ready' | 'failed';

export interface Attachment {
  id: string;
  tenantId: string;
  patientId: string;
  medicalRecordId: string | null;
  kind: AttachmentKind;
  status: AttachmentStatus;
  mimeType: string;
  originalName: string;
  sizeBytes: string | null;
  storageBucket: string;
  storageKey: string;
  etag: string | null;
  sseKmsKeyId: string | null;
  dicomMetadata: Record<string, unknown> | null;
  /** The order this file is filed against — a lab panel or imaging study. */
  linkedOrderId?: string | null;
  linkedOrderType?: 'lab_order' | 'imaging_order' | 'prescription' | null;
  createdAt: string;
  updatedAt: string;
}

export interface PresignUploadInput {
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  medicalRecordId?: string;
}

export interface PresignUploadResponse {
  attachmentId: string;
  uploadUrl: string;
  storageKey: string;
  bucket: string;
  expiresInSeconds: number;
  requiredHeaders: Record<string, string>;
}

interface DownloadResponse {
  url: string;
  expiresInSeconds: number;
  attachment: Attachment;
}

interface ApiOk<T> {
  success: boolean;
  data: T;
}

const unwrap = <T>(payload: T | ApiOk<T>): T => {
  if (payload && typeof payload === 'object' && 'success' in (payload as object) && 'data' in (payload as object)) {
    return (payload as ApiOk<T>).data;
  }
  return payload as T;
};

export const attachmentsApi = {
  list: async (patientId: string): Promise<Attachment[]> => {
    const res = await apiClient.get<ApiOk<Attachment[]> | Attachment[]>(
      `/patients/${patientId}/attachments`,
    );
    return unwrap(res.data);
  },

  presign: async (
    patientId: string,
    input: PresignUploadInput,
  ): Promise<PresignUploadResponse> => {
    const res = await apiClient.post<ApiOk<PresignUploadResponse> | PresignUploadResponse>(
      `/patients/${patientId}/attachments/presign`,
      input,
    );
    return unwrap(res.data);
  },

  /** Frames marked in the study viewer — shown to the patient once reviewed. */
  keyImages: async (orderId: string): Promise<Attachment[]> => {
    const { data } = await apiClient.get(`/imaging-orders/${orderId}/key-images`);
    return (data?.data ?? data) as Attachment[];
  },
  markKeyImage: async (
    orderId: string,
    body: { imageBase64: string; sourceAttachmentId?: string },
  ): Promise<Attachment> => {
    const { data } = await apiClient.post(`/imaging-orders/${orderId}/key-images`, body);
    return (data?.data ?? data) as Attachment;
  },
  unmarkKeyImage: async (orderId: string, attachmentId: string): Promise<void> => {
    await apiClient.delete(`/imaging-orders/${orderId}/key-images/${attachmentId}`);
  },
  finalize: async (
    attachmentId: string,
    input: { etag?: string; dicomMetadata?: Record<string, unknown> } = {},
  ): Promise<Attachment> => {
    const res = await apiClient.post<ApiOk<Attachment> | Attachment>(
      `/attachments/${attachmentId}/finalize`,
      input,
    );
    return unwrap(res.data);
  },

  /**
   * Returns a URL the browser can fetch / embed. The path is the
   * backend's streaming proxy (`/attachments/:id/stream`) — the request
   * is auth'd + tenant-scoped + audited on every read, instead of
   * handing the browser a portable 15-minute presigned MinIO URL.
   *
   * Kept Promise-shaped for compatibility with existing call sites
   * (they `await` it). No network round-trip — the URL is constructed
   * locally; we only fill the `attachment` field if a caller asks for
   * full metadata via the second arg `{ withMetadata: true }`.
   */
  downloadUrl: async (
    attachmentId: string,
    opts: { inline?: boolean; withMetadata?: boolean } = {},
  ): Promise<DownloadResponse> => {
    let attachment: Attachment | undefined;
    if (opts.withMetadata) {
      const meta = await apiClient.get<ApiOk<Attachment> | Attachment>(
        `/attachments/${attachmentId}`,
      );
      attachment = unwrap(meta.data);
    }
    return {
      url: attachmentsApi.streamUrl(attachmentId, { inline: opts.inline }),
      expiresInSeconds: 0,
      attachment: (attachment ?? { id: attachmentId }) as Attachment,
    };
  },

  /**
   * Build the same-origin streaming URL for an attachment. Append the
   * access token as a query param so DOM consumers (`<img src>`,
   * cornerstone's wadouri XHR) can fetch it without being able to
   * attach an Authorization header. The backend accepts either header-
   * or query-based bearer auth.
   *
   * Prefer {@link streamObjectUrl} where possible — putting the full
   * session token in a URL leaks it into history / logs / Referer, and
   * cross-origin `<iframe src>` is blocked by the API's frame headers
   * anyway. This form remains for `<img>`/DICOM consumers that can't
   * take a Blob and aren't subject to framing.
   */
  streamUrl: (
    attachmentId: string,
    opts: { inline?: boolean } = {},
  ): string => {
    const base = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
    const params = new URLSearchParams();
    if (opts.inline) params.set('inline', 'true');
    const token = localStorage.getItem('accessToken');
    if (token) params.set('access_token', token);
    const qs = params.toString();
    return `${base}/attachments/${attachmentId}/stream${qs ? `?${qs}` : ''}`;
  },

  /**
   * Fetch the attachment bytes through the authenticated streaming proxy
   * and hand back a same-origin `blob:` object URL. Goes through
   * `apiClient`, so the bearer rides the Authorization header (no token
   * in any URL) and the request is tenant-scoped + 401-refreshed like
   * every other API call. The resulting `blob:` URL is same-origin to
   * the app, so it embeds in an `<iframe>` without tripping the API's
   * `X-Frame-Options` / CSP `frame-ancestors` (which block the
   * cross-origin `/stream` URL).
   *
   * The caller OWNS the returned URL and MUST `URL.revokeObjectURL()`
   * it when done, or the blob leaks for the page's lifetime.
   */
  streamObjectUrl: async (
    attachmentId: string,
    opts: { inline?: boolean } = {},
  ): Promise<string> => {
    const params = new URLSearchParams();
    if (opts.inline) params.set('inline', 'true');
    const qs = params.toString();
    const res = await apiClient.get<Blob>(
      `/attachments/${attachmentId}/stream${qs ? `?${qs}` : ''}`,
      { responseType: 'blob' },
    );
    return URL.createObjectURL(res.data);
  },

  remove: async (attachmentId: string): Promise<void> => {
    await apiClient.delete(`/attachments/${attachmentId}`);
  },

  /**
   * Run OCR on this attachment. Server gates by `AI_OCR_ENABLED` +
   * `AI_OCR_ATTACHMENTS_ENABLED` and refuses non-image / non-PDF
   * kinds. Read-only — no DB writes — UI shows the text in a copyable
   * box.
   */
  ocr: async (attachmentId: string): Promise<AttachmentOcrResult> => {
    const res = await apiClient.post<
      ApiOk<AttachmentOcrResult> | AttachmentOcrResult
    >(`/attachments/${attachmentId}/ocr`);
    return unwrap(res.data);
  },

  /**
   * Single OCR + LLM call that classifies an attachment
   * (rx / lab / imaging / other) and extracts the matching block in
   * the same response. Server-side suggested-order matching is
   * included for lab/imaging — the FE pre-selects that order in the
   * confirm UI.
   */
  classifyAndExtract: async (
    attachmentId: string,
  ): Promise<AttachmentClassificationResult> => {
    const res = await apiClient.post<
      ApiOk<AttachmentClassificationResult> | AttachmentClassificationResult
    >(`/attachments/${attachmentId}/ai/classify-and-extract`, undefined, {
      // Multi-page PDFs + extraction can run ~30s on cold OCR pod.
      timeout: 90_000,
    });
    return unwrap(res.data);
  },
};

export interface OcrLine {
  text: string;
  confidence: number;
  bbox: number[];
}

export interface AttachmentOcrResult {
  attachmentId: string;
  text: string;
  language: string | null;
  meanConfidence: number;
  lines: OcrLine[];
}
